import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/sonner";
import { VoiceProvider } from "./lib/VoiceContext";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { queryClient } from "./lib/queryClient";
import SidebarLayout from "./components/layout/SidebarLayout";
import { DataRefreshProvider } from "./lib/DataRefreshContext";
import UpdateBanner from "./components/UpdateBanner";
import {
  isChunkLoadError,
  attemptChunkReload,
  clearChunkReloadGuard,
} from "./lib/chunkReload";
import { cleanupExpiredDrafts } from "./hooks/useDraftState";
import { rememberReturnPath } from "./lib/post-login";
import { PushNavigation } from "./lib/push-nav";
import "./App.css";

// Clean up drafts older than 24 hours on app startup
cleanupExpiredDrafts();

// Lazy-loaded pages — split into separate chunks for faster initial load
const Dashboard = lazy(() => import("./pages/Dashboard"));
const MyClients = lazy(() => import("./pages/MyClients"));
const MyTargets = lazy(() => import("./pages/MyTargets"));
const MyReviews = lazy(() => import("./pages/MyReviews"));
const StaffReviews = lazy(() => import("./pages/admin/StaffReviews"));
const TeamAccess = lazy(() => import("./pages/admin/TeamAccess"));
const TendersList = lazy(() => import("./pages/TendersList"));
const TenderDetail = lazy(() => import("./pages/TenderDetail"));
const SearchesList = lazy(() => import("./pages/SearchesList"));
const SearchDetail = lazy(() => import("./pages/SearchDetail"));
const OptionsList = lazy(() => import("./pages/OptionsList"));
const PricingCalculator = lazy(() => import("./pages/PricingCalculator"));
const QuotesList = lazy(() => import("./pages/QuotesList"));
const BookingReview = lazy(() => import("./pages/BookingReview"));
const BatchBookingReview = lazy(() => import("./pages/BatchBookingReview"));
const BookingsList = lazy(() => import("./pages/BookingsList"));
const BookingDetail = lazy(() => import("./pages/BookingDetail"));
const InvoicesList = lazy(() => import("./pages/InvoicesList"));
const PropertiesList = lazy(() => import("./pages/PropertiesList"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const SuppliersList = lazy(() => import("./pages/SuppliersList"));
const SupplierDetail = lazy(() => import("./pages/SupplierDetail"));
const Inbox = lazy(() => import("./pages/Inbox"));
const ClientsList = lazy(() => import("./pages/ClientsList"));
const GuestsList = lazy(() => import("./pages/GuestsList"));
const ClientDetail = lazy(() => import("./pages/ClientDetail"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const Analytics = lazy(() => import("./pages/Analytics"));
const HygieneDetail = lazy(() => import("./pages/HygieneDetail"));
const FinancialsAnalytics = lazy(() => import("./pages/FinancialsAnalytics"));
const Reports = lazy(() => import("./pages/Reports"));
const SupplierPipeline = lazy(() => import("./pages/SupplierPipeline"));
const ClientPipeline = lazy(() => import("./pages/ClientPipeline"));
const PartnerPipeline = lazy(() => import("./pages/PartnerPipeline"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SupplierContractPage = lazy(() => import("./pages/SupplierContractPage"));
const SupplierRegistrationForm = lazy(() => import("./pages/SupplierRegistrationForm"));
const SupplierPropertyForm = lazy(() => import("./pages/SupplierPropertyForm"));
const SupplierReferralAgreement = lazy(() => import("./pages/SupplierReferralAgreement"));
const TenderCommitmentPage = lazy(() => import("./pages/TenderCommitmentPage"));
const FeatureFlags = lazy(() => import("./pages/admin/FeatureFlags"));
const FxRates = lazy(() => import("./pages/admin/FxRates"));
const Approvals = lazy(() => import("./pages/admin/Approvals"));
const BudgetBasisDeclaration = lazy(() => import("./pages/admin/BudgetBasisDeclaration"));
const ClassifiedsReview = lazy(() => import("./pages/admin/ClassifiedsReview"));
const DiagnosticsBrowser = lazy(() => import("./pages/admin/DiagnosticsBrowser"));
const RepairOperations = lazy(() => import("./pages/admin/RepairOperations"));
const OptionStatusMigration = lazy(() => import("./pages/admin/OptionStatusMigration"));
const BookingLifecycleAuditShapeMigration = lazy(() => import("./pages/admin/BookingLifecycleAuditShapeMigration"));
const Operations = lazy(() => import("./pages/Operations"));
const Data = lazy(() => import("./pages/Data"));
const GuestIssues = lazy(() => import("./pages/GuestIssues"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Viewings = lazy(() => import("./pages/Viewings"));

// Some screens link to /enquiries/:id; the enquiry page lives at /searches/:id.
// Without this the catch-all sent those clicks to the dashboard.
function EnquiryLinkRedirect() {
  const { enquiryId } = useParams();
  return <Navigate to={`/searches/${encodeURIComponent(enquiryId)}`} replace />;
}

function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="text-slate-400 text-sm">Loading...</div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
    this.handleManualReload = this.handleManualReload.bind(this);
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }
  componentDidCatch(error, info) {
    console.error("App error:", error, info);
    if (isChunkLoadError(error)) {
      // Auto-reload to pick up the new build. Returns false if a recent
      // attempt already happened — in that case fall through to the manual
      // error UI so we don't loop forever.
      attemptChunkReload();
    }
  }
  handleManualReload() {
    clearChunkReloadGuard();
    window.location.reload();
  }
  render() {
    if (this.state.hasError) {
      const isChunk = this.state.isChunkError;
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center space-y-4 max-w-md px-4">
            <h2 className="text-lg font-semibold text-slate-800">
              {isChunk ? "Update available" : "Something went wrong"}
            </h2>
            <p className="text-sm text-slate-500">
              {isChunk
                ? "We couldn't load the latest version. Please reload the page or clear your cache."
                : (this.state.error?.message || "An unexpected error occurred.")}
            </p>
            <button
              className="px-4 py-2 bg-slate-800 text-white rounded-md text-sm"
              onClick={this.handleManualReload}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    // Come back to this page after signing in (push taps, shared links).
    rememberReturnPath(location.pathname + location.search + location.hash);
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Admin-only pages: a key account manager is redirected to his own portfolio.
// The backend refuses these paths as well, this only avoids dead screens.
function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.is_admin === false) {
    return <Navigate to="/my-clients" replace />;
  }
  return children;
}

function AuthenticatedApp() {
  const { user } = useAuth();
  const home = user && user.is_admin === false ? "/my-clients" : "/dashboard";
  return (
    <VoiceProvider>
      <DataRefreshProvider>
      <PushNavigation />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<SidebarLayout />}>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/my-clients" element={<MyClients />} />
          <Route path="/my-targets" element={<MyTargets />} />
          <Route path="/my-reviews" element={<MyReviews />} />
          <Route path="/admin/staff-reviews" element={<AdminOnly><StaffReviews /></AdminOnly>} />
          <Route path="/admin/staff-reviews/:reviewId" element={<AdminOnly><StaffReviews /></AdminOnly>} />
          <Route path="/dashboard" element={<AdminOnly><Dashboard /></AdminOnly>} />
          <Route path="/searches" element={<SearchesList />} />
          <Route path="/searches/:searchId" element={<SearchDetail />} />
          <Route path="/enquiries/:enquiryId" element={<EnquiryLinkRedirect />} />
          <Route path="/options" element={<OptionsList />} />
          <Route path="/pricing-calculator/:optionId" element={<PricingCalculator />} />
          <Route path="/quotes" element={<QuotesList />} />
          <Route path="/booking-review/:quoteId" element={<BookingReview />} />
          <Route path="/batch-booking-review" element={<BatchBookingReview />} />
          <Route path="/bookings" element={<BookingsList />} />
          <Route path="/bookings/:bookingId" element={<BookingDetail />} />
          <Route path="/invoices" element={<InvoicesList />} />
          <Route path="/properties" element={<PropertiesList />} />
          <Route path="/properties/:propertyId" element={<PropertyDetail />} />
          <Route path="/suppliers" element={<SuppliersList />} />
          <Route path="/suppliers/:supplierId" element={<SupplierDetail />} />
          <Route path="/clients" element={<ClientsList />} />
          <Route path="/clients/:clientId" element={<ClientDetail />} />
          <Route path="/guests" element={<GuestsList />} />
          <Route path="/communications" element={<Inbox />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/integrations" element={<AdminOnly><IntegrationsPage /></AdminOnly>} />
          <Route path="/analytics" element={<AdminOnly><Analytics /></AdminOnly>} />
          <Route path="/financials" element={<AdminOnly><FinancialsAnalytics /></AdminOnly>} />
          <Route path="/reports" element={<AdminOnly><Reports /></AdminOnly>} />
          <Route path="/feedback" element={<Navigate to="/analytics?tab=feedback" replace />} />
          <Route path="/search-analytics" element={<Navigate to="/analytics?tab=search" replace />} />
          <Route path="/hygiene" element={<AdminOnly><HygieneDetail /></AdminOnly>} />
          <Route path="/supplier-pipeline" element={<AdminOnly><SupplierPipeline /></AdminOnly>} />
          <Route path="/client-pipeline" element={<AdminOnly><ClientPipeline /></AdminOnly>} />
          <Route path="/partner-pipeline" element={<AdminOnly><PartnerPipeline /></AdminOnly>} />
          <Route path="/crew-pass-pipeline" element={<Navigate to="/" replace />} />
          <Route path="/tenders" element={<AdminOnly><TendersList /></AdminOnly>} />
          <Route path="/tenders/:tenderId" element={<AdminOnly><TenderDetail /></AdminOnly>} />
          <Route path="/payment-violations" element={<Navigate to="/admin/approvals?tab=payment-violations" replace />} />
          <Route path="/operations" element={<AdminOnly><Operations /></AdminOnly>} />
          <Route path="/data" element={<AdminOnly><Data /></AdminOnly>} />
          <Route path="/settings/feature-flags" element={<AdminOnly><FeatureFlags /></AdminOnly>} />
          <Route path="/admin/approvals" element={<AdminOnly><Approvals /></AdminOnly>} />
          <Route path="/admin/budget-basis" element={<AdminOnly><BudgetBasisDeclaration /></AdminOnly>} />
          <Route path="/admin/classifieds" element={<AdminOnly><ClassifiedsReview /></AdminOnly>} />
          <Route path="/admin/fx-rates" element={<AdminOnly><FxRates /></AdminOnly>} />
          <Route path="/admin/diagnostic-browser" element={<AdminOnly><DiagnosticsBrowser /></AdminOnly>} />
          <Route path="/guest-issues" element={<GuestIssues />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/viewings" element={<Viewings />} />
          <Route path="/admin/repair-ops" element={<AdminOnly><RepairOperations /></AdminOnly>} />
          <Route path="/admin/migrations/option-status" element={<AdminOnly><OptionStatusMigration /></AdminOnly>} />
          <Route path="/admin/migrations/booking-lifecycle-audit-shape" element={<AdminOnly><BookingLifecycleAuditShapeMigration /></AdminOnly>} />
          {/* Redirects from old routes to new tabbed pages */}
          <Route path="/system-health" element={<Navigate to="/operations" replace />} />
          <Route path="/scheduler-health" element={<Navigate to="/operations?tab=schedulers" replace />} />
          <Route path="/admin/diagnostics" element={<Navigate to="/operations?tab=diagnostics" replace />} />
          <Route path="/admin/shadow-audit" element={<Navigate to="/operations?tab=shadow-audit" replace />} />
          <Route path="/api-costs" element={<Navigate to="/operations?tab=costs" replace />} />
          <Route path="/data-hygiene" element={<Navigate to="/data" replace />} />
          <Route path="/qa" element={<Navigate to="/data?tab=qa" replace />} />
          <Route path="/admin/migrations" element={<Navigate to="/data?tab=migrations" replace />} />
          <Route path="/admin/status-repair" element={<Navigate to="/data?tab=migrations" replace />} />
          <Route path="/admin/feature-flags" element={<Navigate to="/settings/feature-flags" replace />} />
          <Route path="/admin/team-access" element={<AdminOnly><TeamAccess /></AdminOnly>} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Route>
      </Routes>
      </Suspense>
      <UpdateBanner />
      </DataRefreshProvider>
    </VoiceProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <div className="App min-h-screen bg-background">
        <AuthProvider>
          <BrowserRouter>
            <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/s/register/:token" element={<SupplierRegistrationForm />} />
              <Route path="/s/contract/:token" element={<SupplierContractPage />} />
              <Route path="/s/referral/:token" element={<SupplierReferralAgreement />} />
              <Route path="/s/commitment/:token" element={<TenderCommitmentPage />} />
              <Route path="/s/properties/:token" element={<SupplierPropertyForm />} />
              <Route path="/*" element={<RequireAuth><AuthenticatedApp /></RequireAuth>} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </BrowserRouter>
          <Toaster position="top-right" />
        </AuthProvider>
      </div>
    </QueryClientProvider>
  );
}

export default App;
